import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, subirArchivo } from "../lib/api";
import { useRefrescarDatos } from "../lib/consultas";
import { creditos, fecha as formatoFecha } from "../lib/formato";
import { Aviso, Cabecera, Campo, Panel } from "../componentes/piezas";
import BuscadorSocios, { type SocioResumen } from "../componentes/BuscadorSocios";
import CapturaImagen from "../componentes/CapturaImagen";
import FirmaPad from "../componentes/FirmaPad";
import { useSesion } from "../lib/sesion";
import { useIdioma, IDIOMAS } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

interface Tipo {
  id: string;
  nombre: string;
  descripcion: string | null;
  activo: boolean;
  descuentoPorcentaje: number;
  cuotaAnualCentimos: number | null;
}

type Paso = "avalista" | "datos" | "documento" | "contrato" | "cuota" | "repaso";

const TITULOS: Record<Paso, Clave> = {
  avalista: "sn.paso.avalista",
  datos: "sn.paso.datos",
  documento: "sn.paso.documento",
  contrato: "sn.paso.contrato",
  cuota: "sn.paso.cuota",
  repaso: "sn.paso.repaso",
};

/**
 * Alta de socio, paso a paso.
 *
 * Antes era un formulario largo con todo a la vez. Dar de alta a alguien no es
 * rellenar una ficha: es una conversación en el mostrador con un orden —quién
 * te avala, quién eres, enséñame el DNI, firma esto, qué cuota te toca— y la
 * pantalla va detrás de esa conversación en vez de al revés.
 *
 * Nada se guarda hasta el último paso. Se puede ir y volver sin miedo, y si
 * alguien cierra a mitad no queda un socio a medio hacer en el libro.
 */
export default function SocioNuevo() {
  const { t } = useIdioma();
  const navegar = useNavigate();
  const refrescarDatos = useRefrescarDatos();
  const { club } = useSesion();

  const [paso, setPaso] = useState<Paso>("avalista");
  /*
   * Hasta donde se ha llegado. Sin esto, volver a un paso anterior a mirar algo
   * bloqueaba todos los de delante y parecia que se habia perdido lo escrito:
   * habia que volver a pasar por todos con «Siguiente».
   */
  const [masLejos, setMasLejos] = useState(0);
  const [avalista, setAvalista] = useState<SocioResumen | null>(null);
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  const [foto, setFoto] = useState<Blob | null>(null);
  const [docFrente, setDocFrente] = useState<Blob | null>(null);
  const [docReverso, setDocReverso] = useState<Blob | null>(null);
  const [sinReverso, setSinReverso] = useState(false);
  const [firma, setFirma] = useState<Blob | null>(null);

  const tipos = useQuery({ queryKey: ["tipos-socio"], queryFn: () => api<Tipo[]>("/tipos-socio") });
  const contrato = useQuery({
    queryKey: ["contrato"],
    queryFn: () => api<{ vigente: { numero: number; texto: string } | null }>("/contrato"),
  });
  const textoContrato = contrato.data?.vigente?.texto ?? null;

  const [datos, setDatos] = useState({
    numeroSocio: "",
    tipoSocioId: "",
    nombre: "",
    apellidos: "",
    documentoTipo: "DNI",
    documentoNumero: "",
    fechaNacimiento: "",
    telefono: "",
    email: "",
    direccion: "",
    ciudad: "",
    cp: "",
    consumoPrevistoMensualG: "30",
    notas: "",
    idioma: "",
    consentimientoRgpd: false,
    cobrarCuota: true,
    metodoPagoCuota: "EFECTIVO",
  });
  const cambiar = (campo: string, valor: string | boolean) => setDatos((d) => ({ ...d, [campo]: valor }));

  // El paso del contrato no existe si el club no ha escrito ninguno.
  const pasos = useMemo<Paso[]>(
    () =>
      (["avalista", "datos", "documento", "contrato", "cuota", "repaso"] as Paso[]).filter(
        (p) => p !== "contrato" || textoContrato,
      ),
    [textoContrato],
  );

  const indice = pasos.indexOf(paso);
  const tipoElegido = tipos.data?.find((t) => t.id === datos.tipoSocioId) ?? null;
  const cuotaCentimos = tipoElegido?.cuotaAnualCentimos ?? club?.cuotaAnualCentimos ?? 0;

  /** Lo que impide dar por bueno un paso. */
  function faltaEn(cual: Paso) {
    if (cual === "datos") {
      if (datos.nombre.trim().length < 2) return t("sn.falta.nombre");
      if (datos.apellidos.trim().length < 2) return t("sn.falta.apellidos");
      if (!datos.fechaNacimiento) return t("sn.falta.nacimiento");
    }
    if (cual === "documento") {
      if (datos.documentoNumero.trim().length < 4) return t("sn.falta.documento");
      /*
       * Las fotos son obligatorias: son la unica forma de comprobar despues que
       * quien entro era quien decia ser. El reverso se puede saltar a proposito
       * -hay pasaportes y permisos que no lo tienen-, pero saltarlo tiene que
       * ser una decision, no un descuido.
       */
      if (!foto) return t("sn.falta.foto");
      if (!docFrente) return t("sn.falta.frente");
      if (!docReverso && !sinReverso) return t("sn.falta.reverso");
    }
    return null;
  }

  /*
   * En el repaso se miran TODOS los pasos, no solo el de ahora: se puede volver
   * atras y borrar el nombre, y crear un socio sin nombre porque el ultimo paso
   * no lo comprobaba seria un fallo tonto de encontrar luego.
   */
  const falta = paso === "repaso" ? (faltaEn("datos") ?? faltaEn("documento")) : faltaEn(paso);

  const irA = (siguiente: Paso) => {
    setPaso(siguiente);
    setMasLejos((m) => Math.max(m, pasos.indexOf(siguiente)));
  };

  async function crear() {
    setError("");
    setEnviando(true);
    try {
      const creado = await api<{ id: string; numeroSocio: number }>("/socios", {
        metodo: "POST",
        cuerpo: {
          numeroSocio: datos.numeroSocio ? Number(datos.numeroSocio) : undefined,
          tipoSocioId: datos.tipoSocioId || null,
          nombre: datos.nombre,
          apellidos: datos.apellidos,
          documentoTipo: datos.documentoTipo,
          documentoNumero: datos.documentoNumero,
          fechaNacimiento: datos.fechaNacimiento,
          telefono: datos.telefono || null,
          email: datos.email || null,
          direccion: datos.direccion || null,
          ciudad: datos.ciudad || null,
          cp: datos.cp || null,
          avalistaId: avalista?.id ?? null,
          consumoPrevistoMensualG: Number(datos.consumoPrevistoMensualG) || 0,
          notas: datos.notas || null,
          idioma: datos.idioma || null,
          consentimientoRgpd: datos.consentimientoRgpd,
          firmaContrato: Boolean(textoContrato && firma),
          cobrarCuota: datos.cobrarCuota,
          metodoPagoCuota: datos.metodoPagoCuota,
        },
      });

      /*
       * Las imágenes van después, porque hasta ahora el socio no existía y no
       * tenían dónde colgarse. Si alguna falla no se tira el alta: el socio ya
       * está bien creado y las fotos se añaden desde su ficha. Perder el alta
       * entera por una foto sería mucho peor que decirlo.
       */
      const fallos: string[] = [];
      const subir = async (cual: string, imagen: Blob | null, que: Clave) => {
        if (!imagen) return;
        try {
          await subirArchivo(`/socios/${creado.id}/imagen/${cual}`, imagen);
        } catch {
          fallos.push(t(que));
        }
      };
      await subir("foto", foto, "sn.subida.foto");
      await subir("documento-frente", docFrente, "sn.subida.frente");
      await subir("documento-reverso", docReverso, "sn.subida.reverso");
      await subir("firma", firma, "sn.subida.firma");

      refrescarDatos();
      navegar(
        `/socios/${creado.id}`,
        fallos.length > 0
          ? { state: { aviso: t("sn.avisoSubida", { que: fallos.join(", ") }) } }
          : undefined,
      );
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("sn.fallo"));
      setPaso("repaso");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <Cabecera titulo={t("sn.titulo")} descripcion={t("sn.descripcion")} />

      <ol className="pasos">
        {pasos.map((p, i) => (
          <li key={p} className={p === paso ? "actual" : i < indice ? "hecho" : undefined}>
            <button type="button" onClick={() => i <= masLejos && setPaso(p)} disabled={i > masLejos}>
              <span className="numero">{i < indice ? "✓" : i + 1}</span>
              {t(TITULOS[p])}
            </button>
          </li>
        ))}
      </ol>

      <Aviso tipo="error">{error}</Aviso>

      {paso === "avalista" && (
        <Panel titulo={t("sn.quienAvala")}>
          <p className="suave" style={{ marginTop: 0 }}>
            {t("sn.avalistaNota")}
          </p>
          {avalista ? (
            <div className="fila separada">
              <div>
                <strong>
                  #{avalista.numeroSocio} {avalista.nombre} {avalista.apellidos}
                </strong>
              </div>
              <button type="button" className="boton secundario" onClick={() => setAvalista(null)}>
                {t("sn.quitar")}
              </button>
            </div>
          ) : (
            <BuscadorSocios alElegir={setAvalista} />
          )}
        </Panel>
      )}

      {paso === "datos" && (
        <div className="rejilla dos" style={{ alignItems: "start" }}>
          <Panel titulo={t("sn.quienEs")}>
            <div className="linea-campos">
              <Campo etiqueta={t("sn.nombre")}>
                <input value={datos.nombre} onChange={(e) => cambiar("nombre", e.target.value)} autoFocus />
              </Campo>
              <Campo etiqueta={t("sn.apellidos")}>
                <input value={datos.apellidos} onChange={(e) => cambiar("apellidos", e.target.value)} />
              </Campo>
            </div>
            <div className="linea-campos">
              <Campo etiqueta={t("sn.nacimiento")}>
                <input
                  type="date"
                  value={datos.fechaNacimiento}
                  onChange={(e) => cambiar("fechaNacimiento", e.target.value)}
                />
              </Campo>
              <Campo etiqueta={t("sn.consumo")} ayuda={t("sn.consumoAyuda")}>
                <input
                  type="number"
                  min={0}
                  value={datos.consumoPrevistoMensualG}
                  onChange={(e) => cambiar("consumoPrevistoMensualG", e.target.value)}
                />
              </Campo>
            </div>
            <div className="linea-campos">
              <Campo etiqueta={t("sn.telefono")}>
                <input value={datos.telefono} onChange={(e) => cambiar("telefono", e.target.value)} />
              </Campo>
              <Campo etiqueta={t("sn.email")}>
                <input type="email" value={datos.email} onChange={(e) => cambiar("email", e.target.value)} />
              </Campo>
            </div>
          </Panel>

          <Panel titulo={t("sn.dondeVive")}>
            <Campo etiqueta={t("sn.direccion")}>
              <input value={datos.direccion} onChange={(e) => cambiar("direccion", e.target.value)} />
            </Campo>
            <div className="linea-campos">
              <Campo etiqueta={t("sn.ciudad")}>
                <input value={datos.ciudad} onChange={(e) => cambiar("ciudad", e.target.value)} />
              </Campo>
              <Campo etiqueta={t("sn.cp")}>
                <input value={datos.cp} onChange={(e) => cambiar("cp", e.target.value)} />
              </Campo>
            </div>
            <Campo etiqueta={t("sn.numeroSocio")} ayuda={t("sn.numeroAyuda")}>
              <input
                type="number"
                min={1}
                value={datos.numeroSocio}
                onChange={(e) => cambiar("numeroSocio", e.target.value)}
              />
            </Campo>
            {/* En blanco = el del club. Se pone aqui porque al socio de fuera
                se lo pregunta quien esta en el mostrador, no el socio. */}
            <Campo etiqueta={t("sn.idioma")} ayuda={t("sn.idiomaAyuda")}>
              <select value={datos.idioma} onChange={(e) => cambiar("idioma", e.target.value)}>
                <option value="">{t("sn.idiomaDelClub")}</option>
                {IDIOMAS.map((i) => (
                  <option key={i.codigo} value={i.codigo} lang={i.codigo}>
                    {i.nombre}
                  </option>
                ))}
              </select>
            </Campo>
            <Campo etiqueta={t("sn.notas")}>
              <textarea rows={2} value={datos.notas} onChange={(e) => cambiar("notas", e.target.value)} />
            </Campo>
          </Panel>
        </div>
      )}

      {paso === "documento" && (
        <Panel titulo={t("sn.documento")}>
          <div className="linea-campos">
            <Campo etiqueta={t("sn.tipo")}>
              <select value={datos.documentoTipo} onChange={(e) => cambiar("documentoTipo", e.target.value)}>
                <option value="DNI">DNI</option>
                <option value="NIE">NIE</option>
                <option value="PASAPORTE">{t("sn.pasaporte")}</option>
              </select>
            </Campo>
            <Campo etiqueta={t("sn.numero")}>
              <input
                value={datos.documentoNumero}
                onChange={(e) => cambiar("documentoNumero", e.target.value.toUpperCase())}
                autoFocus
              />
            </Campo>
          </div>

          <div className="capturas">
            <CapturaImagen
              titulo={t("sn.fotoSocio")}
              ayuda={t("sn.fotoSocioAyuda")}
              imagen={foto}
              alCambiar={setFoto}
              obligatoria
            />
            <CapturaImagen
              titulo={t("sn.docFrente")}
              ayuda={t("sn.docFrenteAyuda")}
              imagen={docFrente}
              alCambiar={setDocFrente}
              trasera
              obligatoria
            />
            <div>
              <CapturaImagen
                titulo={t("sn.docReverso")}
                ayuda={sinReverso ? t("sn.noLoTiene") : t("sn.docReversoAyuda")}
                imagen={docReverso}
                alCambiar={setDocReverso}
                trasera
                obligatoria
                saltada={sinReverso}
              />
              <label className="casilla" style={{ marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={sinReverso}
                  onChange={(e) => {
                    setSinReverso(e.target.checked);
                    if (e.target.checked) setDocReverso(null);
                  }}
                />
                <span className="pequeno">{t("sn.sinReverso")}</span>
              </label>
            </div>
          </div>

          <p className="suave pequeno" style={{ marginBottom: 0 }}>
            {t("sn.fotosHacenFalta")}
          </p>
        </Panel>
      )}

      {paso === "contrato" && textoContrato && (
        <div className="rejilla contrato">
          <Panel titulo={t("sn.loQueFirma")}>
            <pre className="texto-contrato">
              {rellenarPreview(textoContrato, datos, avalista, club?.nombre, t("sn.seAsigna"))}
            </pre>
          </Panel>
          <Panel titulo={t("sn.firma")}>
            <div className="fila separada pequeno" style={{ marginBottom: 10 }}>
              <strong>
                {datos.nombre} {datos.apellidos}
              </strong>
              <span className="suave">{formatoFecha(new Date().toISOString())}</span>
            </div>
            <FirmaPad alFirmar={setFirma} firmada={Boolean(firma)} />
            <label className="casilla" style={{ marginTop: 14 }}>
              <input
                type="checkbox"
                checked={datos.consentimientoRgpd}
                onChange={(e) => cambiar("consentimientoRgpd", e.target.checked)}
              />
              <span className="pequeno">{t("sn.rgpd")}</span>
            </label>
            <p className="suave pequeno" style={{ marginBottom: 0 }}>
              {t("sn.sinFirmaSigue")}
            </p>
          </Panel>
        </div>
      )}

      {paso === "cuota" && (
        <Panel titulo={t("sn.cuota")}>
          <p className="suave" style={{ marginTop: 0 }}>
            {t("sn.cuotaNota")}
          </p>

          <div className="tarjetas-cuota">
            <button
              type="button"
              className={`tarjeta-cuota${datos.tipoSocioId === "" ? " elegida" : ""}`}
              onClick={() => cambiar("tipoSocioId", "")}
            >
              <strong>{t("sn.sinTipo")}</strong>
              <span className="suave pequeno">{t("sn.condicionesGenerales")}</span>
              <span className="precio">{creditos(club?.cuotaAnualCentimos ?? 0)}</span>
              <span className="suave pequeno">{t("sn.alAnio")}</span>
            </button>

            {(tipos.data ?? [])
              .filter((t) => t.activo)
              .map((tipo) => (
                <button
                  key={tipo.id}
                  type="button"
                  className={`tarjeta-cuota${datos.tipoSocioId === tipo.id ? " elegida" : ""}`}
                  onClick={() => cambiar("tipoSocioId", tipo.id)}
                >
                  <strong>{tipo.nombre}</strong>
                  <span className="suave pequeno">
                    {tipo.descuentoPorcentaje > 0
                      ? t("sn.descuentoCatalogo", { porcentaje: tipo.descuentoPorcentaje })
                      : t("sn.sinDescuento")}
                  </span>
                  <span className="precio">
                    {creditos(tipo.cuotaAnualCentimos ?? club?.cuotaAnualCentimos ?? 0)}
                  </span>
                  <span className="suave pequeno">
                    {tipo.cuotaAnualCentimos == null ? t("sn.alAnioLaDelClub") : t("sn.alAnio")}
                  </span>
                </button>
              ))}
          </div>

          <label className="casilla" style={{ marginTop: 18 }}>
            <input
              type="checkbox"
              checked={datos.cobrarCuota}
              onChange={(e) => cambiar("cobrarCuota", e.target.checked)}
            />
            <span>
              {t("sn.cobrarAhora")} <strong>{creditos(cuotaCentimos)}</strong>
              <br />
              <span className="ayuda">{t("sn.sinCobrarla")}</span>
            </span>
          </label>

          {datos.cobrarCuota && cuotaCentimos > 0 && (
            <Campo etiqueta={t("sn.formaDePago")}>
              <select value={datos.metodoPagoCuota} onChange={(e) => cambiar("metodoPagoCuota", e.target.value)}>
                {["EFECTIVO", "TARJETA", "TRANSFERENCIA"].map((m) => (
                  <option key={m} value={m}>
                    {t(`pago.${m}` as Clave)}
                  </option>
                ))}
              </select>
            </Campo>
          )}
        </Panel>
      )}

      {paso === "repaso" && (
        <div className="rejilla dos" style={{ alignItems: "start" }}>
          <Panel titulo={t("sn.repaso")}>
            <dl className="datos">
              <Dato etiqueta={t("sn.r.nombre")} valor={`${datos.nombre} ${datos.apellidos}`} />
              <Dato etiqueta={t("sn.r.documento")} valor={`${datos.documentoTipo} ${datos.documentoNumero}`} />
              <Dato
                etiqueta={t("sn.r.nacimiento")}
                valor={datos.fechaNacimiento ? formatoFecha(datos.fechaNacimiento) : "—"}
              />
              <Dato
                etiqueta={t("sn.r.consumo")}
                valor={t("sn.r.gMes", { n: datos.consumoPrevistoMensualG || 0 })}
              />
              <Dato
                etiqueta={t("sn.r.avalista")}
                valor={avalista ? `#${avalista.numeroSocio} ${avalista.nombre}` : t("sn.r.sinAvalista")}
              />
              <Dato etiqueta={t("sn.r.tipo")} valor={tipoElegido?.nombre ?? t("sn.sinTipo")} />
              <Dato
                etiqueta={t("sn.r.rgpd")}
                valor={datos.consentimientoRgpd ? t("sn.r.firmado") : t("sn.r.pendiente")}
              />
              {textoContrato && (
                <Dato
                  etiqueta={t("sn.r.contrato")}
                  valor={firma ? t("sn.r.firmado") : t("sn.r.pendienteFirma")}
                />
              )}
            </dl>
          </Panel>

          <Panel titulo={t("sn.loQueSeCobra")}>
            <div className="tabla-envoltorio">
              <table className="tabla">
                <tbody>
                  <tr>
                    <td>
                      {t("sn.cuotaAnual")}
                      <div className="suave pequeno">{tipoElegido?.nombre ?? t("sn.sinTipo")}</div>
                    </td>
                    <td className="num tabular">{datos.cobrarCuota ? creditos(cuotaCentimos) : "—"}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>{t("m.total")}</strong>
                    </td>
                    <td className="num tabular">
                      <strong>{datos.cobrarCuota ? creditos(cuotaCentimos) : creditos(0)}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="suave pequeno">
              {!datos.cobrarCuota
                ? t("sn.noSeCobraNada")
                : cuotaCentimos === 0
                  ? t("sn.nadaQueCobrar")
                  : t("sn.seCobraEn", {
                      forma: t(`pago.${datos.metodoPagoCuota}` as Clave).toLowerCase(),
                    })}
            </p>

            <div className="capturas pequenas">
              {(
                [
                  ["sn.mini.socio", foto],
                  ["sn.mini.frente", docFrente],
                  ["sn.mini.reverso", docReverso],
                  ["sn.mini.firma", firma],
                ] as [Clave, Blob | null][]
              ).map(([que, imagen]) => (
                <MiniImagen key={que} titulo={t(que)} imagen={imagen} />
              ))}
            </div>
          </Panel>
        </div>
      )}

      <div className="fila separada" style={{ marginTop: 16 }}>
        <button
          type="button"
          className="boton secundario"
          onClick={() => (indice === 0 ? navegar("/socios") : irA(pasos[indice - 1]!))}
        >
          ← {indice === 0 ? t("g.cancelar") : t("sn.atras")}
        </button>

        <div className="fila">
          {falta && <span className="suave pequeno">{falta}</span>}
          {paso === "repaso" ? (
            <button type="button" className="boton grande" disabled={enviando || Boolean(falta)} onClick={crear}>
              {enviando ? t("sn.creando") : t("sn.crear")}
            </button>
          ) : (
            <button
              type="button"
              className="boton"
              disabled={Boolean(falta)}
              onClick={() => irA(pasos[indice + 1]!)}
            >
              {t("sn.siguiente")} →
            </button>
          )}
        </div>
      </div>
    </>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <>
      <dt>{etiqueta}</dt>
      <dd>{valor}</dd>
    </>
  );
}

/** Miniatura de una de las imágenes, en el repaso. */
function MiniImagen({ titulo, imagen }: { titulo: string; imagen: Blob | null }) {
  const { t } = useIdioma();
  const url = useMemo(() => (imagen ? URL.createObjectURL(imagen) : null), [imagen]);
  return (
    <div className="mini-imagen">
      {url ? <img src={url} alt={titulo} /> : <div className="captura-vacia pequeno">{t("sn.sinFoto")}</div>}
      <span className="suave pequeno">{titulo}</span>
    </div>
  );
}

/**
 * Rellena los huecos del contrato **para verlo antes de firmar**.
 *
 * El de verdad lo rellena el servidor al guardar; esto es la vista previa del
 * mostrador. El número de socio no se puede poner porque todavía no existe: se
 * dice así en vez de dejar el hueco crudo, que en un papel legal parece un
 * error del programa.
 */
function rellenarPreview(
  texto: string,
  datos: { nombre: string; apellidos: string; documentoTipo: string; documentoNumero: string; consumoPrevistoMensualG: string },
  avalista: SocioResumen | null,
  nombreClub: string | undefined,
  seAsigna: string,
) {
  const valores: Record<string, string> = {
    NOMBRE: `${datos.nombre} ${datos.apellidos}`.trim() || "________",
    MEMBER_NAME: `${datos.nombre} ${datos.apellidos}`.trim() || "________",
    DNI: `${datos.documentoTipo} ${datos.documentoNumero}`.trim(),
    NUMERO_SOCIO: seAsigna,
    MEMBER_NUMBER: seAsigna,
    FECHA: formatoFecha(new Date().toISOString()),
    DATE: formatoFecha(new Date().toISOString()),
    CONSUMO_MENSUAL: `${datos.consumoPrevistoMensualG || 0} g`,
    MONTHLY_CONSUMPTION: `${datos.consumoPrevistoMensualG || 0} g`,
    CLUB: nombreClub ?? "",
    CLUB_NAME: nombreClub ?? "",
    AVALISTA: avalista ? `${avalista.nombre} ${avalista.apellidos}` : "—",
  };
  return texto.replace(/\{\{\s*([A-Za-z_]+)\s*\}\}/g, (entero, n: string) => valores[n.toUpperCase()] ?? entero);
}
